import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminBookings } from '@/api/hooks/useAdminBookings';
import { useAdminUnits } from '@/api/hooks/useAdminUnits';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { formatCurrency, formatLocalTime, formatLocalDate } from '@/lib/format';
import i18n from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, ChevronLeft, ChevronRight, MapPin, Download, Loader2 } from 'lucide-react';
import type { BookingStatus } from '@/api/types';
import { useFileDownload } from '@/hooks/useFileDownload';
import WalkInDialog from '@/components/admin/WalkInDialog';
import BookingDetailModal from '@/components/admin/BookingDetailModal';

const STATUS_COLORS: Record<BookingStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  COMPLETED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  EXPIRED: 'bg-yellow-100 text-yellow-800',
};

const PAYMENT_COLORS: Record<string, string> = {
  STRIPE: 'bg-indigo-100 text-indigo-800',
  CASH: 'bg-emerald-100 text-emerald-800',
  TERMINAL: 'bg-cyan-100 text-cyan-800',
  COMP: 'bg-purple-100 text-purple-800',
  PLATFORM_CREDIT: 'bg-amber-100 text-amber-800',
};

const PAYMENT_KEYS: Record<string, string> = {
  STRIPE: 'admin.payment_stripe',
  CASH: 'admin.payment_cash',
  TERMINAL: 'admin.payment_terminal',
  COMP: 'admin.payment_comp',
  PLATFORM_CREDIT: 'admin.payment_platform_credit',
};

const STATUSES: BookingStatus[] = [
  'PENDING', 'CONFIRMED', 'ACTIVE', 'COMPLETED', 'CANCELLED', 'NO_SHOW', 'EXPIRED',
];

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mondayOfWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
}
function sundayOfWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() + (day === 0 ? 0 : 7 - day);
  return new Date(d.getFullYear(), d.getMonth(), diff).toISOString().split('T')[0];
}

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function endOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
}

export default function BookingsPage() {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [locationFilter, setLocationFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  // Debounce search — 300ms delay before firing API call
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { downloadFile, isDownloading } = useFileDownload();
  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];

  const { data: unitsData } = useAdminUnits(
    locationFilter !== 'all' ? locationFilter : undefined
  );
  const units = unitsData?.units ?? [];

  const { data, isLoading } = useAdminBookings({
    startDate,
    endDate,
    locationId: locationFilter !== 'all' ? locationFilter : undefined,
    unitId: unitFilter !== 'all' ? unitFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: debouncedSearch || undefined,
    page,
    size: 20,
  });

  const bookings = data?.bookings ?? [];
  const pagination = data?.pagination;

  const locationLabel =
    locationFilter === 'all'
      ? t('admin.all_locations')
      : locations.find((l) => l.locationId === locationFilter)?.locationName ??
        t('admin.all_locations');

  const statusLabel =
    statusFilter === 'all'
      ? t('admin.all_statuses')
      : t(`booking.status_${statusFilter.toLowerCase()}`);

  const setPreset = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.bookings')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={isDownloading} onClick={() => {
            const params = new URLSearchParams({ startDate, endDate });
            if (locationFilter !== 'all') params.set('locationId', locationFilter);
            downloadFile(`/api/v1/admin/reports/bookings/export?${params.toString()}`, `bookings_${startDate}_${endDate}.csv`);
          }}>
            {isDownloading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} {t('admin.export_csv')}
          </Button>
          <Button size="sm" className="whitespace-nowrap" onClick={() => setWalkInOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('admin.walk_in')}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          {/* Date range */}
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              className="w-36"
            />
            <span className="text-muted-foreground text-sm">—</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              className="w-36"
            />
          </div>

          {/* Quick presets */}
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="whitespace-nowrap" onClick={() => setPreset(today(), today())}>
              {t('admin.preset_today')}
            </Button>
            <Button variant="outline" size="sm" className="whitespace-nowrap" onClick={() => setPreset(mondayOfWeek(), sundayOfWeek())}>
              {t('admin.preset_this_week')}
            </Button>
            <Button variant="outline" size="sm" className="whitespace-nowrap" onClick={() => setPreset(firstOfMonth(), endOfMonth())}>
              {t('admin.preset_this_month')}
            </Button>
          </div>

          {locations.length > 1 && (
            <Select
              value={locationFilter}
              onValueChange={(v) => { setLocationFilter(v ?? 'all'); setUnitFilter('all'); setPage(0); }}
            >
              <SelectTrigger>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm">{locationLabel}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.locationId} value={loc.locationId}>
                    {loc.locationName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {units.length > 0 && (
            <Select
              value={unitFilter}
              onValueChange={(v) => { setUnitFilter(v ?? 'all'); setPage(0); }}
            >
              <SelectTrigger>
                <span className="text-sm">{unitFilter === 'all' ? t('admin.all_units') : units.find(u => u.id === unitFilter)?.name ?? t('admin.all_units')}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_units')}</SelectItem>
                {units.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select
            value={statusFilter}
            onValueChange={(v) => { setStatusFilter(v ?? 'all'); setPage(0); }}
          >
            <SelectTrigger>
              <span className="text-sm">{statusLabel}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_statuses')}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t(`booking.status_${s.toLowerCase()}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder={t('admin.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48"
          />
        </CardContent>
      </Card>

      {/* Bookings table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : bookings.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('admin.no_bookings')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">{t('admin.col_time')}</TableHead>
                    <TableHead className="min-w-[180px]">{t('admin.col_customer')}</TableHead>
                    <TableHead className="hidden md:table-cell min-w-[120px]">{t('admin.col_sport')}</TableHead>
                    <TableHead className="hidden lg:table-cell min-w-[80px]">{t('admin.col_unit')}</TableHead>
                    <TableHead className="hidden sm:table-cell min-w-[70px]">{t('admin.col_duration')}</TableHead>
                    <TableHead className="min-w-[100px]">{t('admin.col_status')}</TableHead>
                    <TableHead className="hidden md:table-cell min-w-[100px]">{t('admin.col_payment')}</TableHead>
                    <TableHead className="text-right min-w-[80px]">{t('admin.col_total')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow
                      key={booking.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedBookingId(booking.id)}
                    >
                      <TableCell className="whitespace-nowrap">
                        <div className="text-sm font-medium">{formatLocalTime(booking.startTimeLocal, i18n.language)}</div>
                        <div className="text-xs text-muted-foreground">{formatLocalDate(booking.startTimeLocal, i18n.language)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{booking.customerName}</div>
                        <div className="text-xs text-muted-foreground">{booking.customerEmail}</div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">{t(`sport_${booking.sportCode}`, { defaultValue: booking.sportName })}</TableCell>
                      <TableCell className="hidden lg:table-cell text-sm">{booking.unitName}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">
                        {t('admin.minutes_short', { count: booking.duration })}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`text-xs min-w-[80px] text-center ${STATUS_COLORS[booking.status]}`}>
                          {t(`booking.status_${booking.status.toLowerCase()}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {booking.paymentMethod && (
                          <Badge variant="secondary" className={`text-xs min-w-[80px] text-center ${PAYMENT_COLORS[booking.paymentMethod] ?? ''}`}>
                            {t(PAYMENT_KEYS[booking.paymentMethod] ?? 'admin.payment_stripe')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(booking.totalPrice, booking.currency, i18n.language)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination — hidden when client-side search is active */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('admin.page_of', { page: pagination.page + 1, total: pagination.totalPages })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={pagination.page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="mr-1 h-4 w-4" /> {t('admin.previous')}
            </Button>
            <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              {t('admin.next')} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <WalkInDialog open={walkInOpen} onOpenChange={setWalkInOpen} />
      <BookingDetailModal
        bookingId={selectedBookingId}
        open={!!selectedBookingId}
        onOpenChange={(open) => { if (!open) setSelectedBookingId(null); }}
      />
    </div>
  );
}
